import { useState, useRef } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Camera, CalendarIcon, X, Upload, ImagePlus } from "lucide-react";
import { cn as cnUtil } from "@/lib/utils";
import { cn } from "@/lib/utils";
import type { MetricEntry } from "./MetricsSummaryCards";

interface PhotoPreview {
  file: File;
  preview: string;
}

interface Props {
  initialWeight?: string;
  onAdd: (entry: MetricEntry, photos: { type: "front" | "side" | "back"; file: File }[]) => void;
  onCancel: () => void;
  uploading?: boolean;
}

const fields = [
  { key: "weight", label: "Peso (kg) *", placeholder: "75.0", required: true, min: 30, max: 300 },
  { key: "arm", label: "Braço relaxado (cm)", placeholder: "35", min: 15, max: 60 },
  { key: "waist", label: "Cintura (cm)", placeholder: "80", min: 40, max: 200 },
  { key: "abdomen", label: "Abdômen (cm)", placeholder: "85", min: 40, max: 200 },
  { key: "hip", label: "Quadril (cm)", placeholder: "95", min: 50, max: 200 },
  { key: "thigh", label: "Coxa (cm)", placeholder: "55", min: 30, max: 100 },
  { key: "calf", label: "Panturrilha (cm)", placeholder: "35", min: 20, max: 70 },
] as const;

const photoTypes = [
  { key: "front" as const, label: "Frente" },
  { key: "side" as const, label: "Lado" },
  { key: "back" as const, label: "Costas" },
];

const DropzoneCell = ({ label, onFile, onClick }: { label: string; onFile: (f: File) => void; onClick: () => void }) => {
  const [dragging, setDragging] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); }}
      onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); }}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onDrop={(e) => {
        e.preventDefault(); e.stopPropagation(); setDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file && file.type.startsWith("image/")) onFile(file);
      }}
      className={cnUtil(
        "w-full aspect-[3/4] rounded-xl border-2 border-dashed transition-all duration-200 flex flex-col items-center justify-center gap-1.5",
        dragging
          ? "border-primary bg-primary/5 scale-[1.02]"
          : "border-border hover:border-primary/40 text-muted-foreground"
      )}
    >
      <div className={cnUtil(
        "w-9 h-9 rounded-full flex items-center justify-center transition-colors",
        dragging ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
      )}>
        {dragging ? <Upload className="w-4 h-4" /> : <Camera className="w-4 h-4" />}
      </div>
      <span className={cnUtil("text-xs font-medium", dragging ? "text-primary" : "")}>{dragging ? "Solte aqui" : label}</span>
    </button>
  );
};

const MetricsForm = ({ initialWeight, onAdd, onCancel, uploading }: Props) => {
  const today = new Date();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date(today.getFullYear(), today.getMonth(), today.getDate()));
  const [formData, setFormData] = useState<Record<string, string>>({
    weight: initialWeight || "",
    arm: "", waist: "", abdomen: "", hip: "", thigh: "", calf: "", note: "",
  });
  const [photos, setPhotos] = useState<Record<string, PhotoPreview | null>>({
    front: null, side: null, back: null,
  });
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({ front: null, side: null, back: null });

  const handlePhotoSelect = (type: string) => {
    fileRefs.current[type]?.click();
  };

  const handleFileChange = (type: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setPhotos(prev => ({ ...prev, [type]: { file, preview } }));
  };

  const removePhoto = (type: string) => {
    if (photos[type]?.preview) URL.revokeObjectURL(photos[type]!.preview);
    setPhotos(prev => ({ ...prev, [type]: null }));
  };

  const handleSubmit = () => {
    if (!formData.weight || !selectedDate) return;
    const entry: MetricEntry = {
      id: Date.now().toString(),
      date: `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`,
      weight: Number(formData.weight),
      arm: formData.arm ? Number(formData.arm) : undefined,
      waist: formData.waist ? Number(formData.waist) : undefined,
      abdomen: formData.abdomen ? Number(formData.abdomen) : undefined,
      hip: formData.hip ? Number(formData.hip) : undefined,
      thigh: formData.thigh ? Number(formData.thigh) : undefined,
      calf: formData.calf ? Number(formData.calf) : undefined,
      note: formData.note || undefined,
    };

    const photoFiles = photoTypes
      .filter(p => photos[p.key])
      .map(p => ({ type: p.key, file: photos[p.key]!.file }));

    onAdd(entry, photoFiles);
  };

  const inputClass = "w-full bg-muted border border-border rounded-xl px-4 py-3 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30";

  return (
    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-card rounded-2xl p-5 shadow-card border border-primary/30 space-y-4">
      <h3 className="font-semibold text-foreground">Novo Registro</h3>

      {/* Date picker */}
      <div>
        <label className="text-sm text-muted-foreground mb-1 block">Data de Registro *</label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal rounded-xl px-4 py-3 h-auto border-border bg-muted",
                !selectedDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {selectedDate ? format(selectedDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR }) : <span>Selecione a data</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(new Date(date.getFullYear(), date.getMonth(), date.getDate()))}
              disabled={(date) => {
                const todayStart = new Date();
                todayStart.setHours(0, 0, 0, 0);
                return date > todayStart;
              }}
              initialFocus
              className={cn("p-3 pointer-events-auto")}
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Measurements */}
      <div className="grid grid-cols-2 gap-3">
        {fields.map(f => (
          <div key={f.key}>
            <label className="text-sm text-muted-foreground mb-1 block">{f.label}</label>
            <input
              type="number"
              step="0.1"
              min={f.min}
              max={f.max}
              value={formData[f.key]}
              onChange={e => {
                const value = e.target.value;
                // Permitir campo vazio ou apenas números (incluindo ponto decimal)
                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                  setFormData(prev => ({ ...prev, [f.key]: value }));
                }
              }}
              onBlur={e => {
                const numValue = parseFloat(e.target.value);
                // Validar limites ao sair do campo
                if (!isNaN(numValue)) {
                  if (numValue < f.min) {
                    setFormData(prev => ({ ...prev, [f.key]: f.min.toString() }));
                  } else if (numValue > f.max) {
                    setFormData(prev => ({ ...prev, [f.key]: f.max.toString() }));
                  }
                }
              }}
              placeholder={f.placeholder}
              inputMode="decimal"
              className={inputClass}
            />
          </div>
        ))}
      </div>

      <div>
        <label className="text-sm text-muted-foreground mb-1 block">Observação</label>
        <input
          type="text"
          value={formData.note}
          onChange={e => setFormData(prev => ({ ...prev, note: e.target.value }))}
          placeholder="Ex: Após treino, em jejum..."
          className={inputClass}
        />
      </div>

      {/* Progress Photos */}
      <div className="space-y-2">
        <label className="text-sm text-muted-foreground flex items-center gap-1.5">
          <Camera className="w-4 h-4" /> Fotos de Progresso (opcional)
        </label>
        <div className="grid grid-cols-3 gap-3">
          {photoTypes.map(p => (
            <div key={p.key} className="relative">
              <input
                ref={el => { fileRefs.current[p.key] = el; }}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={e => handleFileChange(p.key, e)}
              />
              {photos[p.key] ? (
                <div className="relative aspect-[3/4] rounded-xl overflow-hidden border border-border group">
                  <img src={photos[p.key]!.preview} alt={p.label} className="w-full h-full object-cover" />
                  <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">{p.label}</span>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => removePhoto(p.key)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity bg-destructive text-destructive-foreground rounded-full p-1.5 shadow-lg"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ) : (
                <DropzoneCell
                  label={p.label}
                  onFile={(file) => {
                    const preview = URL.createObjectURL(file);
                    setPhotos(prev => ({ ...prev, [p.key]: { file, preview } }));
                  }}
                  onClick={() => handlePhotoSelect(p.key)}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-3">
        <Button onClick={handleSubmit} className="flex-1 gradient-primary text-primary-foreground" disabled={!formData.weight || !selectedDate || uploading}>
          {uploading ? "Salvando..." : "Salvar Registro"}
        </Button>
        <Button onClick={onCancel} variant="outline" disabled={uploading}>Cancelar</Button>
      </div>
    </motion.div>
  );
};

export default MetricsForm;
